import { useState, useEffect, useRef } from "react";
import { useAtom } from "jotai";
import { motion, AnimatePresence } from "framer-motion";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Button from "@mui/material/Button";
import LinearProgress from "@mui/material/LinearProgress";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import AddIcon from "@mui/icons-material/Add";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import DeleteIcon from "@mui/icons-material/Delete";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import LinkIcon from "@mui/icons-material/Link";
import { themeAtom, isAdminAtom } from "../../../../-Atoms";
import UniversalTextField from "../../../../GlobalComponents/UniversalTextField";
import { db, doc, updateDoc, storage } from "../../../../firebaseConfig";
import {
  ref as storageRef,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import { v4 as uuidv4 } from "uuid";

const OutsourcerBriefsContainer = ({
  jobData,
  clientData,
  setHasUnsavedChanges,
}) => {
  const [theme] = useAtom(themeAtom);
  const [isAdmin] = useAtom(isAdminAtom);

  const [clientBriefs, setClientBriefs] = useState(
    clientData?.outsourcerBriefs || []
  );
  const [jobBriefs, setJobBriefs] = useState(jobData?.outsourcerBriefs || []);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isDragOver, setIsDragOver] = useState(false);
  const [dragCounter, setDragCounter] = useState(0);
  const [nameDialog, setNameDialog] = useState({
    open: false,
    files: [],
    isClient: false,
  });
  const [fileName, setFileName] = useState("");
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    brief: null,
    index: null,
    isClient: false,
  });

  const fileInputRef = useRef(null);
  const clientFileInputRef = useRef(null);
  const dropZoneRef = useRef(null);

  // Update state when props change
  useEffect(() => {
    setClientBriefs(clientData?.outsourcerBriefs || []);
    setJobBriefs(jobData?.outsourcerBriefs || []);
  }, [jobData, clientData]);

  // Drag and drop handlers
  useEffect(() => {
    const handleDragEnter = (e) => {
      e.preventDefault();
      e.stopPropagation();
      setDragCounter((prev) => prev + 1);
      if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
        setIsDragOver(true);
      }
    };

    const handleDragLeave = (e) => {
      e.preventDefault();
      e.stopPropagation();
      setDragCounter((prev) => prev - 1);
      if (dragCounter <= 1) {
        setIsDragOver(false);
      }
    };

    const handleDragOver = (e) => {
      e.preventDefault();
      e.stopPropagation();
    };

    const handleDrop = (e) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragOver(false);
      setDragCounter(0);

      const files = Array.from(e.dataTransfer.files);
      if (files.length > 0) {
        // Default to job briefs for drag & drop
        setNameDialog({ open: true, files, isClient: false });
        setFileName(files[0].name.replace(/\.[^/.]+$/, ""));
      }
    };

    const dropZone = dropZoneRef.current;
    if (dropZone) {
      dropZone.addEventListener("dragenter", handleDragEnter);
      dropZone.addEventListener("dragleave", handleDragLeave);
      dropZone.addEventListener("dragover", handleDragOver);
      dropZone.addEventListener("drop", handleDrop);
    }

    return () => {
      if (dropZone) {
        dropZone.removeEventListener("dragenter", handleDragEnter);
        dropZone.removeEventListener("dragleave", handleDragLeave);
        dropZone.removeEventListener("dragover", handleDragOver);
        dropZone.removeEventListener("drop", handleDrop);
      }
    };
  }, [dragCounter]);

  const handleAddBrief = async (isClientBrief, isUrl = true) => {
    if (!isAdmin) return;

    if (isUrl) {
      try {
        if (isClientBrief) {
          const newClientBriefs = [
            ...clientBriefs,
            {
              name: "",
              url: "",
              isFile: false,
              uploadedAt: new Date().toISOString(),
              default: true,
            },
          ];
          setClientBriefs(newClientBriefs);
          await updateDoc(doc(db, "ClientDatabase", clientData.id), {
            outsourcerBriefs: newClientBriefs,
          });
        } else {
          const newJobBriefs = [
            ...jobBriefs,
            {
              name: "",
              url: "",
              isFile: false,
              uploadedAt: new Date().toISOString(),
            },
          ];
          setJobBriefs(newJobBriefs);
          await updateDoc(doc(db, "JobData", jobData.id), {
            outsourcerBriefs: newJobBriefs,
          });
        }
      } catch (error) {
        console.error("Error adding brief:", error);
      }
    } else {
      // File upload
      if (isClientBrief) {
        clientFileInputRef.current?.click();
      } else {
        fileInputRef.current?.click();
      }
    }
  };

  const handleFileSelect = (e, isClient) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      setNameDialog({ open: true, files, isClient });
      setFileName(files[0].name.replace(/\.[^/.]+$/, ""));
    }
  };

  const handleFileUpload = async () => {
    if (!nameDialog.files || nameDialog.files.length === 0 || !fileName.trim())
      return;

    const { files, isClient } = nameDialog;
    const name = fileName;

    // Close dialog first
    setNameDialog({ open: false, files: [], isClient: false });
    setFileName("");

    setIsUploading(true);
    setUploadProgress(0);

    try {
      const file = files[0];
      const fileExtension = file.name.match(/\.[^/.]+$/)?.[0] || "";
      const uniqueId = uuidv4().slice(0, 8);
      const storageFileName = `${name}_${uniqueId}${fileExtension}`;

      if (file.size > 150000000) {
        alert("File must be under 150MB");
        return;
      }

      // Upload to Firebase Storage
      const storagePath = isClient
        ? `OutsourcerBriefs/ClientBriefs/${clientData.id}/${storageFileName}`
        : `OutsourcerBriefs/JobBriefs/${jobData.id}/${storageFileName}`;
      const fileStorageRef = storageRef(storage, storagePath);
      await uploadBytes(fileStorageRef, file);
      const downloadURL = await getDownloadURL(fileStorageRef);

      const newBrief = {
        name: name.trim(),
        url: downloadURL,
        isFile: true,
        uploadedAt: new Date().toISOString(),
        originalFileName: file.name,
        size: file.size,
        storagePath: storagePath,
      };

      if (isClient) {
        newBrief.default = true;
        const newClientBriefs = [...clientBriefs, newBrief];
        setClientBriefs(newClientBriefs);
        await updateDoc(doc(db, "ClientDatabase", clientData.id), {
          outsourcerBriefs: newClientBriefs,
        });
      } else {
        const newJobBriefs = [...jobBriefs, newBrief];
        setJobBriefs(newJobBriefs);
        await updateDoc(doc(db, "JobData", jobData.id), {
          outsourcerBriefs: newJobBriefs,
        });
      }

      setUploadProgress(100);

      // Reset file inputs
      if (fileInputRef.current) fileInputRef.current.value = "";
      if (clientFileInputRef.current) clientFileInputRef.current.value = "";
    } catch (error) {
      console.error("Error uploading file:", error);
      alert("Error uploading file. Please try again.");
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const handleDeleteBrief = async (index, isClientBrief) => {
    if (!isAdmin) return;

    const brief = isClientBrief ? clientBriefs[index] : jobBriefs[index];

    // If it's a file, show confirmation dialog
    if (brief.isFile) {
      setDeleteDialog({ open: true, brief, index, isClient: isClientBrief });
      return;
    }

    // For URL entries, delete immediately
    try {
      if (isClientBrief) {
        const newClientBriefs = [...clientBriefs];
        newClientBriefs.splice(index, 1);
        setClientBriefs(newClientBriefs);
        await updateDoc(doc(db, "ClientDatabase", clientData.id), {
          outsourcerBriefs: newClientBriefs,
        });
      } else {
        const newJobBriefs = [...jobBriefs];
        newJobBriefs.splice(index, 1);
        setJobBriefs(newJobBriefs);
        await updateDoc(doc(db, "JobData", jobData.id), {
          outsourcerBriefs: newJobBriefs,
        });
      }
    } catch (error) {
      console.error("Error deleting brief:", error);
    }
  };

  const handleConfirmDelete = async () => {
    const { brief, index, isClient } = deleteDialog;

    try {
      // Delete from Firebase Storage if it's a file
      if (brief.isFile && brief.url) {
        let pathToDelete = brief.storagePath;

        // Fallback for existing files without storagePath
        if (!pathToDelete) {
          // Extract filename from the download URL
          const url = new URL(brief.url);
          const pathname = decodeURIComponent(url.pathname);
          // Firebase Storage URLs have format: /v0/b/{bucket}/o/{path}
          const pathMatch = pathname.match(/\/o\/(.+)$/);
          if (pathMatch) {
            pathToDelete = pathMatch[1];
          } else {
            // Last resort: reconstruct from available data
            const fileName = brief.originalFileName || "unknown";
            pathToDelete = isClient
              ? `OutsourcerBriefs/ClientBriefs/${clientData.id}/${fileName}`
              : `OutsourcerBriefs/JobBriefs/${jobData.id}/${fileName}`;
          }
        }

        if (pathToDelete) {
          const fileRef = storageRef(storage, pathToDelete);
          await deleteObject(fileRef);
        }
      }

      // Delete from database
      if (isClient) {
        const newClientBriefs = [...clientBriefs];
        newClientBriefs.splice(index, 1);
        setClientBriefs(newClientBriefs);
        await updateDoc(doc(db, "ClientDatabase", clientData.id), {
          outsourcerBriefs: newClientBriefs,
        });
      } else {
        const newJobBriefs = [...jobBriefs];
        newJobBriefs.splice(index, 1);
        setJobBriefs(newJobBriefs);
        await updateDoc(doc(db, "JobData", jobData.id), {
          outsourcerBriefs: newJobBriefs,
        });
      }
    } catch (error) {
      console.error("Error deleting file:", error);
      alert("Error deleting file. Please try again.");
    }

    setDeleteDialog({ open: false, brief: null, index: null, isClient: false });
  };

  const handleUpdateBrief = async (index, field, value, isClientBrief) => {
    if (!isAdmin) return;

    try {
      if (isClientBrief) {
        const newClientBriefs = [...clientBriefs];
        newClientBriefs[index] = { ...newClientBriefs[index], [field]: value };
        setClientBriefs(newClientBriefs);
        await updateDoc(doc(db, "ClientDatabase", clientData.id), {
          outsourcerBriefs: newClientBriefs,
        });
      } else {
        const newJobBriefs = [...jobBriefs];
        newJobBriefs[index] = { ...newJobBriefs[index], [field]: value };
        setJobBriefs(newJobBriefs);
        await updateDoc(doc(db, "JobData", jobData.id), {
          outsourcerBriefs: newJobBriefs,
        });
      }
    } catch (error) {
      console.error("Error updating brief:", error);
    }
  };

  const handleOpenBrief = (url) => {
    if (!url) return;

    let formattedUrl = url;
    if (!/^https?:\/\//i.test(url)) {
      formattedUrl = `https://${url}`;
    }

    window.open(formattedUrl, "_blank");
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.3,
        ease: "easeInOut",
        staggerChildren: 0.05,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.3,
        ease: [0.25, 0.1, 0.25, 1.0],
      },
    },
    hover: { scale: 1.02 },
    tap: { scale: 0.98 },
  };

  const renderBriefSection = (briefs, label, isClientBrief) => {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1,
          width: "100%",
          borderRadius: "10px",
          overflow: "hidden",
          backgroundColor:
            theme === "dark" ? "rgba(35,35,35,0.7)" : "rgba(235,235,235,0.9)",
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          boxShadow:
            theme === "dark"
              ? "0 2px 10px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.1)"
              : "0 2px 10px rgba(0,0,0,0.05), inset 0 1px 0 rgba(255,255,255,0.5)",
          padding: 1.5,
          mb: 2,
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 1,
          }}
        >
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: "bold",
              color:
                theme === "dark" ? "rgba(255,255,255,0.9)" : "rgba(0,0,0,0.9)",
            }}
          >
            {label}
          </Typography>

          {isAdmin && (
            <Box sx={{ display: "flex", gap: 0.5 }}>
              <IconButton
                size="small"
                onClick={() => handleAddBrief(isClientBrief, true)}
                title="Add URL"
                sx={{
                  bgcolor:
                    theme === "dark"
                      ? "rgba(60,60,60,0.8)"
                      : "rgba(240,240,240,0.8)",
                  color:
                    theme === "dark"
                      ? "rgba(255,255,255,0.8)"
                      : "rgba(0,0,0,0.7)",
                  boxShadow:
                    theme === "dark"
                      ? "0 1px 3px rgba(0,0,0,0.3)"
                      : "0 1px 3px rgba(0,0,0,0.1)",
                  "&:hover": {
                    bgcolor:
                      theme === "dark"
                        ? "rgba(70,70,70,0.9)"
                        : "rgba(245,245,245,0.9)",
                  },
                }}
              >
                <AddIcon fontSize="small" />
              </IconButton>

              <IconButton
                size="small"
                onClick={() => handleAddBrief(isClientBrief, false)}
                title="Upload File"
                sx={{
                  bgcolor:
                    theme === "dark"
                      ? "rgba(60,60,60,0.8)"
                      : "rgba(240,240,240,0.8)",
                  color:
                    theme === "dark"
                      ? "rgba(255,255,255,0.8)"
                      : "rgba(0,0,0,0.7)",
                  boxShadow:
                    theme === "dark"
                      ? "0 1px 3px rgba(0,0,0,0.3)"
                      : "0 1px 3px rgba(0,0,0,0.1)",
                  "&:hover": {
                    bgcolor:
                      theme === "dark"
                        ? "rgba(70,70,70,0.9)"
                        : "rgba(245,245,245,0.9)",
                  },
                }}
              >
                <AttachFileIcon fontSize="small" />
              </IconButton>
            </Box>
          )}
        </Box>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {briefs &&
            briefs?.map((brief, index) => (
              <motion.div
                key={index}
                variants={itemVariants}
                style={{ marginBottom: "8px" }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 0.5,
                    width: "100%",
                  }}
                >
                  {/* Main row with icon, text field(s), and buttons - always centered */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      width: "100%",
                    }}
                  >
                    {/* Type indicator icon */}
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 32,
                        height: 32,
                        borderRadius: "50%",
                        bgcolor: isClientBrief
                          ? theme === "dark"
                            ? "rgba(33, 150, 243, 0.15)"
                            : "rgba(33, 150, 243, 0.1)"
                          : theme === "dark"
                          ? "rgba(76, 175, 80, 0.15)"
                          : "rgba(76, 175, 80, 0.1)",
                        border: isClientBrief
                          ? theme === "dark"
                            ? "1px solid rgba(33, 150, 243, 0.3)"
                            : "1px solid rgba(33, 150, 243, 0.2)"
                          : theme === "dark"
                          ? "1px solid rgba(76, 175, 80, 0.3)"
                          : "1px solid rgba(76, 175, 80, 0.2)",
                        boxShadow:
                          theme === "dark"
                            ? "0 1px 3px rgba(0,0,0,0.3)"
                            : "0 1px 2px rgba(0,0,0,0.1)",
                        flexShrink: 0,
                      }}
                    >
                      {brief.isFile ? (
                        <InsertDriveFileIcon
                          sx={{
                            fontSize: "1rem",
                            color: isClientBrief
                              ? theme === "dark"
                                ? "#64b5f6"
                                : "#2196f3"
                              : theme === "dark"
                              ? "#81c784"
                              : "#4caf50",
                          }}
                        />
                      ) : (
                        <LinkIcon
                          sx={{
                            fontSize: "1rem",
                            color: isClientBrief
                              ? theme === "dark"
                                ? "#64b5f6"
                                : "#2196f3"
                              : theme === "dark"
                              ? "#81c784"
                              : "#4caf50",
                          }}
                        />
                      )}
                    </Box>

                    {/* Text fields */}
                    {!brief.isFile ? (
                      <>
                        <UniversalTextField
                          value={brief.name || ""}
                          placeholder="Brief name"
                          hiddenLabel
                          disabled={!isAdmin}
                          setHasUnsavedChanges={setHasUnsavedChanges}
                          onCallback={(newValue) => {
                            handleUpdateBrief(
                              index,
                              "name",
                              newValue,
                              isClientBrief
                            );
                            return newValue;
                          }}
                          sx={{
                            marginTop: 0,
                            flex: 1,
                            "& .MuiInputBase-root": {
                              fontSize: "0.85rem",
                              height: "40px",
                              padding: "8px 12px",
                            },
                          }}
                        />
                        <UniversalTextField
                          value={brief.url || ""}
                          placeholder="URL"
                          hiddenLabel
                          disabled={!isAdmin}
                          setHasUnsavedChanges={setHasUnsavedChanges}
                          onCallback={(newValue) => {
                            handleUpdateBrief(
                              index,
                              "url",
                              newValue,
                              isClientBrief
                            );
                            return newValue;
                          }}
                          sx={{
                            marginTop: 0,
                            flex: 1,
                            "& .MuiInputBase-root": {
                              fontSize: "0.8rem",
                              height: "40px",
                              padding: "8px 12px",
                            },
                          }}
                        />
                      </>
                    ) : (
                      <UniversalTextField
                        value={brief.name || ""}
                        placeholder="Brief name"
                        hiddenLabel
                        disabled={!isAdmin}
                        setHasUnsavedChanges={setHasUnsavedChanges}
                        onCallback={(newValue) => {
                          handleUpdateBrief(
                            index,
                            "name",
                            newValue,
                            isClientBrief
                          );
                          return newValue;
                        }}
                        sx={{
                          marginTop: 0,
                          flex: 1,
                          "& .MuiInputBase-root": {
                            fontSize: "0.85rem",
                            height: "40px",
                            padding: "8px 12px",
                          },
                        }}
                      />
                    )}

                    {isClientBrief && (
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={brief.default ?? true}
                            onChange={(e) =>
                              handleUpdateBrief(
                                index,
                                "default",
                                e.target.checked,
                                isClientBrief
                              )
                            }
                            disabled={!isAdmin}
                            size="small"
                          />
                        }
                        label={
                          <Typography sx={{ fontSize: "0.8rem" }}>
                            Default
                          </Typography>
                        }
                        sx={{
                          ml: "auto",
                          mr: 0.5,
                          "& .MuiSvgIcon-root": { fontSize: "1.2rem" },
                        }}
                      />
                    )}

                    {/* Action buttons */}
                    {(brief.url || brief.isFile) && (
                      <IconButton
                        size="small"
                        onClick={() => handleOpenBrief(brief.url)}
                        sx={{
                          bgcolor:
                            theme === "dark"
                              ? "rgba(60,60,60,0.8)"
                              : "rgba(240,240,240,0.8)",
                          color:
                            theme === "dark"
                              ? "rgba(255,255,255,0.8)"
                              : "rgba(0,0,0,0.7)",
                          boxShadow:
                            theme === "dark"
                              ? "0 1px 3px rgba(0,0,0,0.3)"
                              : "0 1px 3px rgba(0,0,0,0.1)",
                          "&:hover": {
                            bgcolor:
                              theme === "dark"
                                ? "rgba(70,70,70,0.9)"
                                : "rgba(245,245,245,0.9)",
                          },
                        }}
                      >
                        <OpenInNewIcon fontSize="small" />
                      </IconButton>
                    )}

                    <IconButton
                      size="small"
                      onClick={() => handleDeleteBrief(index, isClientBrief)}
                      disabled={!isAdmin}
                      sx={{
                        bgcolor:
                          theme === "dark"
                            ? "rgba(60,60,60,0.8)"
                            : "rgba(240,240,240,0.8)",
                        color:
                          theme === "dark"
                            ? "rgba(255,255,255,0.8)"
                            : "rgba(0,0,0,0.7)",
                        boxShadow:
                          theme === "dark"
                            ? "0 1px 3px rgba(0,0,0,0.3)"
                            : "0 1px 3px rgba(0,0,0,0.1)",
                        "&:hover": {
                          bgcolor:
                            theme === "dark"
                              ? "rgba(70,70,70,0.9)"
                              : "rgba(245,245,245,0.9)",
                        },
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>

                  {/* Filename subtitle for file entries */}
                  {brief.isFile && brief.originalFileName && (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        ml: 5,
                      }}
                    >
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          fontSize: "0.75rem",
                          fontStyle: "italic",
                        }}
                      >
                        {brief.originalFileName}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </motion.div>
            ))}
        </motion.div>
      </Box>
    );
  };

  // Defensive check to prevent component from disappearing
  if (!jobData || !clientData) {
    return (
      <Box
        sx={{
          width: "100%",
          marginTop: 2,
          position: "relative",
        }}
      >
        <Box sx={{ padding: 2, opacity: 0.5 }}>Loading briefs...</Box>
      </Box>
    );
  }

  return (
    <Box
      ref={dropZoneRef}
      sx={{
        position: "relative",
        width: "100%",
        px: 1,
        mt: 2,
      }}
    >
      {/* Drag and Drop Overlay */}
      <AnimatePresence>
        {isDragOver && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 1000,
              background:
                theme === "dark"
                  ? "rgba(144, 202, 249, 0.1)"
                  : "rgba(25, 118, 210, 0.1)",
              border: `2px dashed ${theme === "dark" ? "#90caf9" : "#1976d2"}`,
              borderRadius: 10,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backdropFilter: "blur(4px)",
            }}
          >
            <Box
              sx={{
                textAlign: "center",
                color: theme === "dark" ? "#90caf9" : "#1976d2",
              }}
            >
              <CloudUploadIcon sx={{ fontSize: 48, mb: 1 }} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Drop files to upload
              </Typography>
            </Box>
          </motion.div>
        )}
      </AnimatePresence>

      <Box
        sx={{
          width: "100%",
          position: "relative",
        }}
      >
        {/* Upload Progress */}
        <AnimatePresence>
          {isUploading && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              style={{ marginBottom: 16 }}
            >
              <LinearProgress
                variant="determinate"
                value={uploadProgress}
                sx={{
                  borderRadius: 1,
                  height: 6,
                  backgroundColor:
                    theme === "dark"
                      ? "rgba(255, 255, 255, 0.1)"
                      : "rgba(0, 0, 0, 0.1)",
                }}
              />
              <Typography
                variant="caption"
                sx={{ mt: 1, display: "block", textAlign: "center" }}
              >
                Uploading... {Math.round(uploadProgress)}%
              </Typography>
            </motion.div>
          )}
        </AnimatePresence>

        {renderBriefSection(clientBriefs, "Client Briefs", true)}
        {renderBriefSection(jobBriefs, "Job Briefs", false)}

        {/* Hidden file inputs */}
        <input
          type="file"
          ref={fileInputRef}
          style={{ display: "none" }}
          onChange={(e) => handleFileSelect(e, false)}
        />
        <input
          type="file"
          ref={clientFileInputRef}
          style={{ display: "none" }}
          onChange={(e) => handleFileSelect(e, true)}
        />

        {/* Name Dialog */}
        <Dialog
          open={nameDialog.open}
          onClose={() =>
            setNameDialog({ open: false, files: [], isClient: false })
          }
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Name Your File</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="File Name"
              fullWidth
              variant="outlined"
              value={fileName}
              onChange={(e) => setFileName(e.target.value)}
              helperText="Enter a descriptive name for this file"
            />
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() =>
                setNameDialog({ open: false, files: [], isClient: false })
              }
            >
              Cancel
            </Button>
            <Button
              onClick={handleFileUpload}
              variant="contained"
              disabled={!fileName.trim()}
            >
              Upload
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteDialog.open}
          onClose={() =>
            setDeleteDialog({
              open: false,
              brief: null,
              index: null,
              isClient: false,
            })
          }
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Delete File</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete "
              {deleteDialog.brief?.name || "this file"}"? This will permanently
              remove the file from storage and cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() =>
                setDeleteDialog({
                  open: false,
                  brief: null,
                  index: null,
                  isClient: false,
                })
              }
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmDelete}
              variant="contained"
              color="error"
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
};

export default OutsourcerBriefsContainer;